/**
 * CampingPanel — Camping screen shown when the party sets up camp.
 *
 * Layout:
 *   [CAMP]  [Night/Day status]  [Close]
 *   ──────────────────────────────────────────────────
 *   PARTY ROLES — avatar + name/class, role dropdown
 *   ──────────────────────────────────────────────────
 *   CAMP EQUIPMENT — checkboxes + Rations counter
 *   Encounter chance: live-updated
 *   ──────────────────────────────────────────────────
 *   [Forage]  [Rest Until Morning]  [Break Camp]
 *   Inline result messages
 *
 * Integrates with CampingSystem when available; falls back gracefully.
 * Rations are read from WorldState.rationPoints (never hardcoded).
 * Emits 'camp:exit' on Break Camp.
 *
 * @module CampingPanel
 */

import { UIPanel }  from './UIPanel.js';
import DebugLog     from '../debug/DebugLog.js';
import EventBus     from '../core/EventBus.js';
import WorldState   from '../world/WorldState.js';

// ─── Style injection ──────────────────────────────────────────────────────────

const STYLE_ID = 'spellroads-camping-style';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* ══════════════════════════════════════════════════════════════
       CampingPanel — Spellroads
       ══════════════════════════════════════════════════════════════ */

    #panel-camping {
      position: fixed;
      inset: 0;
      z-index: 218;
      display: none;
      flex-direction: column;
      background: rgba(8, 6, 4, 0.97);
      pointer-events: all;
      overflow: hidden;
      font-family: 'Georgia', 'Times New Roman', serif;
      color: #e8d5b0;
    }

    #panel-camping.visible { display: flex; }

    /* ── Header ─────────────────────────────────────────────────── */
    .camp-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px);
      background: rgba(0, 0, 0, 0.55);
      border-bottom: 1px solid rgba(200, 160, 32, 0.25);
      flex-shrink: 0;
      gap: 12px;
    }

    .camp-header-left {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .camp-title {
      font-size: clamp(0.9rem, 1.8vw, 1.2rem);
      font-weight: 700;
      color: #c8a020;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .camp-time-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: clamp(0.6rem, 0.95vw, 0.74rem);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      padding: 3px 9px;
      border-radius: 3px;
      border: 1px solid rgba(32, 128, 192, 0.45);
      background: rgba(32, 128, 192, 0.1);
      color: #2080c0;
      white-space: nowrap;
    }

    .camp-time-badge.is-night {
      border-color: rgba(200, 160, 32, 0.45);
      background: rgba(200, 160, 32, 0.1);
      color: #c8a020;
    }

    .camp-close {
      background: none;
      border: 1px solid rgba(200, 160, 32, 0.3);
      color: #a09070;
      cursor: pointer;
      width: 44px;
      height: 44px;
      min-width: 44px;
      min-height: 44px;
      border-radius: 4px;
      font-size: 1.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.15s, border-color 0.15s;
      flex-shrink: 0;
    }

    .camp-close:hover,
    .camp-close:focus-visible {
      color: #c8a020;
      border-color: #c8a020;
      outline: 2px solid #e8c040;
      outline-offset: 2px;
    }

    /* ── Scrollable body ─────────────────────────────────────────── */
    .camp-body {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    /* ── Section blocks ──────────────────────────────────────────── */
    .camp-section {
      padding: clamp(12px, 2vw, 20px) clamp(16px, 2.5vw, 28px);
      border-bottom: 1px solid rgba(200, 160, 32, 0.12);
    }

    .camp-section:last-child {
      border-bottom: none;
    }

    .camp-section-heading {
      font-size: clamp(0.62rem, 0.9vw, 0.72rem);
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #706050;
      margin-bottom: clamp(8px, 1.2vh, 14px);
    }

    /* ── Loading placeholder ─────────────────────────────────────── */
    .camp-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      font-size: clamp(0.8rem, 1.2vw, 0.95rem);
      color: #706050;
      font-style: italic;
    }

    /* ── Party role rows ─────────────────────────────────────────── */
    .camp-party-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .camp-member-row {
      display: grid;
      grid-template-columns: 52px 1fr auto;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      background: rgba(255, 255, 255, 0.025);
      border: 1px solid rgba(200, 160, 32, 0.1);
      border-radius: 4px;
      min-height: 60px;
      transition: background 0.12s, border-color 0.12s;
    }

    .camp-member-row:hover {
      background: rgba(200, 160, 32, 0.04);
      border-color: rgba(200, 160, 32, 0.2);
    }

    .camp-member-avatar {
      width: 44px;
      height: 44px;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.35);
      border: 1px solid rgba(200, 160, 32, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      flex-shrink: 0;
    }

    .camp-member-info {
      min-width: 0;
    }

    .camp-member-name {
      font-size: clamp(0.72rem, 1.15vw, 0.85rem);
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #e8d5b0;
    }

    .camp-member-class {
      font-size: clamp(0.58rem, 0.85vw, 0.68rem);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #706050;
      margin-top: 2px;
    }

    .camp-member-skill-hint {
      font-size: clamp(0.58rem, 0.82vw, 0.66rem);
      color: #40a060;
      margin-top: 3px;
    }

    /* Role dropdown */
    .camp-role-select {
      background: rgba(26, 21, 16, 0.9);
      border: 1px solid rgba(200, 160, 32, 0.3);
      border-radius: 4px;
      color: #e8d5b0;
      font-family: 'Georgia', 'Times New Roman', serif;
      font-size: clamp(0.65rem, 1vw, 0.78rem);
      padding: 0 8px;
      height: 44px;
      min-width: 130px;
      cursor: pointer;
      appearance: auto;
      transition: border-color 0.15s;
    }

    .camp-role-select:hover,
    .camp-role-select:focus-visible {
      border-color: #c8a020;
      outline: 2px solid #e8c040;
      outline-offset: 2px;
    }

    .camp-role-select option {
      background: #1a1510;
      color: #e8d5b0;
    }

    /* ── Equipment + Rations row ─────────────────────────────────── */
    .camp-equip-rations-row {
      display: flex;
      align-items: flex-start;
      gap: clamp(16px, 3vw, 32px);
      flex-wrap: wrap;
    }

    .camp-equip-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
      min-width: 180px;
    }

    .camp-equip-item {
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 44px;
      cursor: pointer;
    }

    .camp-equip-item.unavailable {
      opacity: 0.38;
      cursor: not-allowed;
    }

    .camp-equip-checkbox {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
      accent-color: #c8a020;
      cursor: pointer;
    }

    .camp-equip-item.unavailable .camp-equip-checkbox {
      cursor: not-allowed;
    }

    .camp-equip-label {
      font-size: clamp(0.7rem, 1.1vw, 0.82rem);
      color: #a09070;
      user-select: none;
      line-height: 1.3;
    }

    .camp-equip-item:not(.unavailable):hover .camp-equip-label,
    .camp-equip-item:not(.unavailable) input:checked + .camp-equip-label {
      color: #e8d5b0;
    }

    .camp-equip-badge {
      font-size: 0.6rem;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      padding: 1px 5px;
      border-radius: 2px;
      background: rgba(200, 48, 32, 0.12);
      border: 1px solid rgba(200, 48, 32, 0.3);
      color: #c83020;
    }

    /* Rations counter */
    .camp-rations-block {
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: flex-end;
      flex-shrink: 0;
    }

    .camp-rations-label {
      font-size: clamp(0.6rem, 0.9vw, 0.7rem);
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: #706050;
    }

    .camp-rations-value {
      font-size: clamp(1.1rem, 2.2vw, 1.5rem);
      font-weight: 700;
      color: #c8a020;
      line-height: 1;
    }

    .camp-rations-value.low {
      color: #c83020;
    }

    .camp-rations-unit {
      font-size: clamp(0.6rem, 0.85vw, 0.7rem);
      color: #706050;
    }

    /* ── Encounter chance bar ────────────────────────────────────── */
    .camp-encounter-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: clamp(8px, 1.2vh, 12px);
      flex-wrap: wrap;
    }

    .camp-encounter-label {
      font-size: clamp(0.62rem, 0.9vw, 0.72rem);
      color: #706050;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      flex-shrink: 0;
    }

    .camp-encounter-bar-wrap {
      flex: 1;
      min-width: 100px;
      height: 8px;
      background: rgba(255, 255, 255, 0.06);
      border-radius: 4px;
      overflow: hidden;
      border: 1px solid rgba(200, 160, 32, 0.12);
    }

    .camp-encounter-bar {
      height: 100%;
      border-radius: 4px;
      background: #40a060;
      transition: width 0.3s ease, background 0.3s ease;
    }

    .camp-encounter-bar.medium { background: #e07820; }
    .camp-encounter-bar.high   { background: #c83020; }

    .camp-encounter-pct {
      font-size: clamp(0.68rem, 1vw, 0.78rem);
      font-weight: 700;
      color: #e8d5b0;
      min-width: 42px;
      text-align: right;
      flex-shrink: 0;
    }

    /* ── Footer actions ──────────────────────────────────────────── */
    .camp-footer {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: clamp(8px, 1.5vw, 16px);
      padding: clamp(10px, 1.8vh, 18px) clamp(16px, 2.5vw, 28px);
      background: rgba(0, 0, 0, 0.35);
      border-top: 1px solid rgba(200, 160, 32, 0.15);
      flex-shrink: 0;
      flex-wrap: wrap;
    }

    .camp-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-height: 44px;
      padding: 8px clamp(14px, 2vw, 22px);
      background: rgba(42, 32, 24, 0.9);
      border: 1px solid rgba(200, 160, 32, 0.3);
      border-radius: 4px;
      color: #c8d0a0;
      font-family: 'Georgia', 'Times New Roman', serif;
      font-size: clamp(0.65rem, 1vw, 0.78rem);
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      cursor: pointer;
      transition: background 0.12s, border-color 0.12s, color 0.12s;
      white-space: nowrap;
    }

    .camp-btn:hover,
    .camp-btn:focus-visible {
      background: rgba(200, 160, 32, 0.12);
      border-color: rgba(200, 160, 32, 0.6);
      color: #c8a020;
      outline: 2px solid #e8c040;
      outline-offset: 2px;
    }

    .camp-btn:disabled {
      opacity: 0.38;
      cursor: not-allowed;
      pointer-events: none;
    }

    .camp-btn--primary {
      background: rgba(200, 160, 32, 0.15);
      border-color: rgba(200, 160, 32, 0.6);
      color: #c8a020;
      font-size: clamp(0.7rem, 1.1vw, 0.84rem);
      padding: 8px clamp(18px, 2.5vw, 30px);
    }

    .camp-btn--primary:hover,
    .camp-btn--primary:focus-visible {
      background: rgba(200, 160, 32, 0.25);
      border-color: #e8c040;
      color: #e8c040;
    }

    .camp-btn--danger {
      border-color: rgba(200, 48, 32, 0.4);
      color: #c83020;
    }

    .camp-btn--danger:hover,
    .camp-btn--danger:focus-visible {
      background: rgba(200, 48, 32, 0.12);
      border-color: rgba(200, 48, 32, 0.7);
      color: #e84030;
    }

    /* ── Result message area ─────────────────────────────────────── */
    .camp-result {
      padding: clamp(8px, 1.2vh, 12px) clamp(16px, 2.5vw, 28px);
      font-size: clamp(0.7rem, 1.1vw, 0.82rem);
      color: #a09070;
      background: rgba(0, 0, 0, 0.2);
      border-top: 1px solid rgba(200, 160, 32, 0.08);
      min-height: 36px;
      display: flex;
      align-items: center;
      gap: 8px;
      line-height: 1.5;
      transition: color 0.2s;
      flex-shrink: 0;
    }

    .camp-result:empty { display: none; }
    .camp-result.success { color: #40a060; }
    .camp-result.error   { color: #c83020; }
    .camp-result.warn    { color: #e07820; }
    .camp-result.info    { color: #2080c0; }

    /* ── Confirmation overlay ─────────────────────────────────────── */
    .camp-confirm-overlay {
      position: absolute;
      inset: 0;
      background: rgba(8, 6, 4, 0.88);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 20px;
      z-index: 10;
      padding: 20px;
    }

    .camp-confirm-card {
      background: #2a2018;
      border: 1px solid rgba(200, 160, 32, 0.3);
      border-radius: 6px;
      padding: clamp(20px, 3vh, 32px) clamp(24px, 3.5vw, 40px);
      display: flex;
      flex-direction: column;
      gap: 16px;
      max-width: min(480px, 90vw);
      text-align: center;
    }

    .camp-confirm-title {
      font-size: clamp(0.85rem, 1.5vw, 1rem);
      font-weight: 700;
      color: #c8a020;
      text-transform: uppercase;
      letter-spacing: 0.07em;
    }

    .camp-confirm-desc {
      font-size: clamp(0.72rem, 1.1vw, 0.84rem);
      color: #a09070;
      line-height: 1.6;
    }

    .camp-confirm-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;
    }

    /* ══════════════════════════════════════════════════════════════
       Responsive — Mobile portrait: roles stack, footer wraps
       ══════════════════════════════════════════════════════════════ */
    @media (max-width: 767px) and (orientation: portrait) {
      .camp-member-row {
        grid-template-columns: 44px 1fr;
        grid-template-rows: auto auto;
      }

      .camp-role-select {
        grid-column: 1 / -1;
        min-width: 0;
        width: 100%;
      }

      .camp-equip-rations-row {
        flex-direction: column;
        gap: 12px;
      }

      .camp-rations-block {
        align-items: flex-start;
        flex-direction: row;
        align-items: center;
        gap: 10px;
      }

      .camp-rations-label { order: 0; }
      .camp-rations-value { order: 1; }
      .camp-rations-unit  { order: 2; }

      .camp-footer {
        flex-direction: column;
        align-items: stretch;
      }

      .camp-btn {
        width: 100%;
        justify-content: center;
      }
    }

    /* Mobile landscape */
    @media (max-width: 1023px) and (orientation: landscape) {
      .camp-member-row {
        min-height: 52px;
      }

      .camp-footer {
        padding: 8px 16px;
      }
    }
  `;
  document.head.appendChild(style);
}

// ─── Placeholder party (when CharacterSystem not yet loaded) ──────────────────

const PLACEHOLDER_PARTY = [
  { id: 'p1', name: 'Sigrid',  cls: 'Warrior',    avatar: '\u2694',  isHero: true },
  { id: 'p2', name: 'Bjorn',   cls: 'Scout',      avatar: '\uD83C\uDFF9', isHero: true },
  { id: 'p3', name: 'Thyra',   cls: 'Runecaster', avatar: '\u{1F52E}', isHero: true },
];

// ─── Role options ─────────────────────────────────────────────────────────────

/** @type {Array<{value:string, label:string, hint:string}>} */
const ROLE_OPTIONS = [
  { value: '',         label: 'No Role',   hint: '' },
  { value: 'scout',    label: 'Scout',     hint: 'Perception: encounter -30%' },
  { value: 'cook',     label: 'Cook',      hint: 'Survival: rations feed +1' },
  { value: 'guard',    label: 'Guard',     hint: 'Athletics: encounter -20%' },
  { value: 'medic',    label: 'Medic',     hint: 'Medicine: heal party 25% HP' },
  { value: 'engineer', label: 'Engineer',  hint: 'Crafting: repairs equipment' },
];

// ─── Camp equipment definitions ───────────────────────────────────────────────

const EQUIPMENT_DEFS = [
  { key: 'hasTent',      label: 'Tent',        icon: '\u26FA', hint: 'Encounter -10%' },
  { key: 'hasAlarmWire', label: 'Alarm Wire',   icon: '\uD83D\uDD14', hint: 'Encounter -15%' },
  { key: 'hasPalisade',  label: 'Palisade',     icon: '\uD83E\uDEB5', hint: 'Encounter -20%' },
];

// ─── CampingPanel ─────────────────────────────────────────────────────────────

class CampingPanel extends UIPanel {
  constructor() {
    injectStyles();
    super('panel-camping', { trapFocus: false, zLayer: 'panel' });

    /**
     * Map of charId => role string ('' = none).
     * @type {Map<string, string>}
     */
    this._roleAssignments = new Map();

    /**
     * Equipment state driven from WorldState.campEquipment, toggled live.
     * @type {{ hasTent: boolean, hasAlarmWire: boolean, hasPalisade: boolean }}
     */
    this._equipment = { hasTent: false, hasAlarmWire: false, hasPalisade: false };

    /** @type {Array} Cached party list for current session. */
    this._party = [];

    /** @type {Function|null} EventBus unsub handle. */
    this._unsub = null;
  }

  // ─── UIPanel lifecycle ───────────────────────────────────────────────────

  show() {
    this._loadParty();
    this._loadEquipment();
    this._render();
    super.show();
    this._bindEvents();
    DebugLog.info('[camp] CampingPanel opened');
  }

  hide() {
    super.hide();
    DebugLog.info('[camp] CampingPanel closed');
  }

  destroy() {
    if (this._unsub) { this._unsub(); this._unsub = null; }
    super.destroy();
  }

  // ─── Data loading ────────────────────────────────────────────────────────

  /**
   * Load party from CharacterSystem or fall back to placeholder.
   */
  _loadParty() {
    try {
      const CS = window.__characterSystem;
      if (CS && typeof CS.getAll === 'function') {
        const chars = CS.getAll();
        if (Array.isArray(chars) && chars.length > 0) {
          this._party = chars;
          return;
        }
      }
    } catch (e) {
      DebugLog.warn(`[camp] CharacterSystem not available, using placeholders: ${e.message}`);
    }
    this._party = PLACEHOLDER_PARTY;
  }

  /**
   * Load equipment state from WorldState.campEquipment (or CampingSystem state).
   */
  _loadEquipment() {
    try {
      const CS = window.__campingSystem;
      const state = CS?.getCampState?.();
      if (state?.options) {
        this._equipment = {
          hasTent:      state.options.hasTent      ?? false,
          hasAlarmWire: state.options.hasAlarmWire ?? false,
          hasPalisade:  state.options.hasPalisade  ?? false,
        };
        return;
      }
    } catch (_) { /* system not loaded */ }

    // Fallback: read from WorldState
    const we = WorldState.campEquipment ?? {};
    this._equipment = {
      hasTent:      we.hasTent      ?? false,
      hasAlarmWire: we.hasAlarmWire ?? false,
      hasPalisade:  we.hasPalisade  ?? false,
    };
  }

  /**
   * Get current ration points from WorldState.
   * @returns {number}
   */
  _getRationPoints() {
    return WorldState.rationPoints ?? 0;
  }

  /**
   * Get owned equipment from WorldState (items that have been purchased/acquired).
   * @returns {{ hasTent: boolean, hasAlarmWire: boolean, hasPalisade: boolean }}
   */
  _getOwnedEquipment() {
    const inv = WorldState.campEquipmentOwned ?? WorldState.inventory ?? null;

    // If WorldState has explicit owned flags, use them
    if (WorldState.campEquipmentOwned) {
      return {
        hasTent:      WorldState.campEquipmentOwned.hasTent      ?? true,
        hasAlarmWire: WorldState.campEquipmentOwned.hasAlarmWire ?? true,
        hasPalisade:  WorldState.campEquipmentOwned.hasPalisade  ?? false,
      };
    }

    // Default: tent and alarm wire are commonly owned; palisade is rare
    return { hasTent: true, hasAlarmWire: true, hasPalisade: false };
  }

  /**
   * Retrieve time string from WorldState / TimeSystem for header badge.
   * @returns {{ label: string, isNight: boolean }}
   */
  _getTimeInfo() {
    try {
      const TS = window.__timeSystem;
      if (TS && typeof TS.getTime === 'function') {
        const t = TS.getTime();
        const h = t.hour ?? 0;
        const isNight = h < 6 || h >= 20;
        const period  = isNight ? 'Night' : 'Day';
        const hStr    = String(h).padStart(2, '0');
        const mStr    = String(t.minute ?? 0).padStart(2, '0');
        return { label: `${period} — ${hStr}:${mStr}`, isNight };
      }
    } catch (_) { /* not loaded */ }

    const t = WorldState.time ?? { hour: 20, minute: 0 };
    const isNight = (t.hour ?? 20) < 6 || (t.hour ?? 20) >= 20;
    const period  = isNight ? 'Night' : 'Day';
    const hStr    = String(t.hour ?? 20).padStart(2, '0');
    const mStr    = String(t.minute ?? 0).padStart(2, '0');
    return { label: `${period} — ${hStr}:${mStr}`, isNight };
  }

  /**
   * Calculate current night encounter chance percentage string.
   * Delegates to CampingSystem if available, otherwise estimates locally.
   * @returns {number} 0–100 integer percent
   */
  _calcEncounterPct() {
    try {
      const CS = window.__campingSystem;
      if (CS && typeof CS.getNightEncounterChance === 'function') {
        return Math.round(CS.getNightEncounterChance() * 100);
      }
    } catch (_) { /* not loaded */ }

    // Local estimate
    const regionLevel = WorldState.currentRegionLevel ?? 1;
    let base = 0.10 + 0.03 * Math.max(0, regionLevel - 1);
    let reduction = 0;

    for (const [, role] of this._roleAssignments) {
      if (role === 'scout') reduction += 0.30;
      if (role === 'guard') reduction += 0.20;
    }

    if (this._equipment.hasTent)       reduction += 0.10;
    if (this._equipment.hasAlarmWire)  reduction += 0.15;
    if (this._equipment.hasPalisade)   reduction += 0.20;

    return Math.round(Math.max(0.01, Math.min(1.0, base - reduction)) * 100);
  }

  /**
   * Check if CampingSystem is available.
   * @returns {boolean}
   */
  _hasCampingSystem() {
    try {
      return !!(window.__campingSystem && typeof window.__campingSystem.restUntilMorning === 'function');
    } catch (_) {
      return false;
    }
  }

  // ─── DOM build ───────────────────────────────────────────────────────────

  _buildHTML() {
    return `
      <div class="camp-header">
        <div class="camp-header-left">
          <div class="camp-title">Camp</div>
          <div class="camp-time-badge" id="camp-time-badge">Night</div>
        </div>
        <button class="camp-close" id="camp-close-btn" aria-label="Close camping panel" tabindex="0">&times;</button>
      </div>

      <div class="camp-body" id="camp-body">
        <div class="camp-loading">Camping system loading...</div>
      </div>

      <div class="camp-result" id="camp-result" aria-live="polite"></div>

      <div class="camp-footer" id="camp-footer">
        <button class="camp-btn" id="camp-forage-btn" tabindex="0">Forage</button>
        <button class="camp-btn camp-btn--primary" id="camp-rest-btn" tabindex="0">Rest Until Morning</button>
        <button class="camp-btn camp-btn--danger" id="camp-break-btn" tabindex="0">Break Camp</button>
      </div>
    `;
  }

  // ─── Full render ──────────────────────────────────────────────────────────

  _render() {
    this._renderTimeBadge();
    this._renderBody();
    this._refreshFooterButtons();
  }

  _renderTimeBadge() {
    const badge = this.el.querySelector('#camp-time-badge');
    if (!badge) return;
    const { label, isNight } = this._getTimeInfo();
    badge.textContent = label;
    badge.classList.toggle('is-night', isNight);
  }

  _renderBody() {
    const body = this.el.querySelector('#camp-body');
    if (!body) return;

    if (!this._hasCampingSystem()) {
      body.innerHTML = `
        <div class="camp-loading">Camping system loading... Role assignments will take effect on rest.</div>
      `;
      // Render party + equipment even without CampingSystem (graceful degradation)
    }

    body.innerHTML = '';

    // ── Party Roles section ──────────────────────────────────────
    const rolesSection = this._buildRolesSection();
    body.appendChild(rolesSection);

    // ── Equipment + Rations section ──────────────────────────────
    const equipSection = this._buildEquipmentSection();
    body.appendChild(equipSection);
  }

  /**
   * Build the Party Roles section DOM element.
   * @returns {HTMLElement}
   */
  _buildRolesSection() {
    const section = document.createElement('div');
    section.className = 'camp-section';

    const heading = document.createElement('div');
    heading.className = 'camp-section-heading';
    heading.textContent = 'Party Roles';
    section.appendChild(heading);

    const list = document.createElement('div');
    list.className = 'camp-party-list';
    list.id = 'camp-party-list';
    section.appendChild(list);

    if (this._party.length === 0) {
      list.innerHTML = '<div style="color:#706050;font-style:italic;font-size:0.8rem">No party members found.</div>';
      return section;
    }

    for (const member of this._party) {
      list.appendChild(this._buildMemberRow(member));
    }

    return section;
  }

  /**
   * Build a single party member row.
   * @param {object} member
   * @returns {HTMLElement}
   */
  _buildMemberRow(member) {
    const charId  = member.id ?? member.charId ?? String(Math.random());
    const name    = member.name ?? 'Unknown';
    const cls     = member.cls ?? member.class ?? member.className ?? '';
    const avatar  = member.avatar ?? member.icon ?? '\uD83D\uDC64';
    const curRole = this._roleAssignments.get(charId) ?? '';

    // Build role skill hint based on selected role
    const roleOpt = ROLE_OPTIONS.find(r => r.value === curRole);
    const skillHint = roleOpt?.hint ?? '';

    const row = document.createElement('div');
    row.className = 'camp-member-row';
    row.dataset.charId = charId;

    // Avatar
    const avatarEl = document.createElement('div');
    avatarEl.className = 'camp-member-avatar';
    avatarEl.setAttribute('aria-hidden', 'true');
    avatarEl.textContent = typeof avatar === 'string' ? avatar : '\uD83D\uDC64';

    // Info block
    const info = document.createElement('div');
    info.className = 'camp-member-info';
    info.innerHTML = `
      <div class="camp-member-name">${this._esc(name)}</div>
      ${cls ? `<div class="camp-member-class">${this._esc(cls)}</div>` : ''}
      <div class="camp-member-skill-hint" id="camp-hint-${this._esc(charId)}">${this._esc(skillHint)}</div>
    `;

    // Role dropdown
    const select = document.createElement('select');
    select.className = 'camp-role-select';
    select.id = `camp-role-${this._esc(charId)}`;
    select.setAttribute('aria-label', `Role for ${name}`);
    select.dataset.charId = charId;

    for (const opt of ROLE_OPTIONS) {
      const option = document.createElement('option');
      option.value = opt.value;
      option.textContent = opt.label;
      if (opt.value === curRole) option.selected = true;
      select.appendChild(option);
    }

    row.appendChild(avatarEl);
    row.appendChild(info);
    row.appendChild(select);
    return row;
  }

  /**
   * Build the Camp Equipment + Rations section DOM element.
   * @returns {HTMLElement}
   */
  _buildEquipmentSection() {
    const section = document.createElement('div');
    section.className = 'camp-section';

    const heading = document.createElement('div');
    heading.className = 'camp-section-heading';
    heading.textContent = 'Camp Equipment';
    section.appendChild(heading);

    const row = document.createElement('div');
    row.className = 'camp-equip-rations-row';
    section.appendChild(row);

    // Equipment checkboxes
    const equip = document.createElement('div');
    equip.className = 'camp-equip-list';
    equip.id = 'camp-equip-list';

    const owned = this._getOwnedEquipment();

    for (const def of EQUIPMENT_DEFS) {
      const isOwned   = owned[def.key] ?? false;
      const isChecked = this._equipment[def.key] && isOwned;

      const item = document.createElement('label');
      item.className = 'camp-equip-item' + (isOwned ? '' : ' unavailable');
      item.htmlFor = `camp-equip-${def.key}`;

      const checkbox = document.createElement('input');
      checkbox.type      = 'checkbox';
      checkbox.id        = `camp-equip-${def.key}`;
      checkbox.className = 'camp-equip-checkbox';
      checkbox.dataset.equipKey = def.key;
      checkbox.checked  = isChecked;
      checkbox.disabled = !isOwned;
      checkbox.setAttribute('aria-label', def.label + (isOwned ? '' : ' (not owned)'));

      const labelText = document.createElement('span');
      labelText.className = 'camp-equip-label';
      labelText.textContent = `${def.icon}  ${def.label}  (${def.hint})`;

      item.appendChild(checkbox);
      item.appendChild(labelText);

      if (!isOwned) {
        const badge = document.createElement('span');
        badge.className = 'camp-equip-badge';
        badge.textContent = 'Not owned';
        item.appendChild(badge);
      }

      equip.appendChild(item);
    }
    row.appendChild(equip);

    // Rations block
    const rations = WorldState.rationPoints ?? 0;
    const isLow   = rations < (this._party.length || 1);

    const rationBlock = document.createElement('div');
    rationBlock.className = 'camp-rations-block';
    rationBlock.innerHTML = `
      <div class="camp-rations-label">Rations</div>
      <div class="camp-rations-value${isLow ? ' low' : ''}" id="camp-rations-val">${rations}</div>
      <div class="camp-rations-unit">pts</div>
    `;
    row.appendChild(rationBlock);

    // Encounter chance bar
    const pct = this._calcEncounterPct();
    const barClass = pct >= 40 ? 'high' : pct >= 20 ? 'medium' : '';

    const encounterRow = document.createElement('div');
    encounterRow.className = 'camp-encounter-row';
    encounterRow.innerHTML = `
      <span class="camp-encounter-label">Encounter chance</span>
      <div class="camp-encounter-bar-wrap" role="progressbar"
           aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100"
           aria-label="Night encounter chance ${pct}%">
        <div class="camp-encounter-bar ${barClass}" id="camp-enc-bar"
             style="width:${pct}%"></div>
      </div>
      <span class="camp-encounter-pct" id="camp-enc-pct">${pct}%</span>
    `;
    section.appendChild(encounterRow);

    return section;
  }

  // ─── Live encounter chance update ─────────────────────────────────────────

  /**
   * Recalculate and update encounter chance display without full re-render.
   */
  _refreshEncounterChance() {
    const pct      = this._calcEncounterPct();
    const bar      = this.el.querySelector('#camp-enc-bar');
    const pctEl    = this.el.querySelector('#camp-enc-pct');
    const barWrap  = this.el.querySelector('.camp-encounter-bar-wrap');
    const barClass = pct >= 40 ? 'high' : pct >= 20 ? 'medium' : '';

    if (bar) {
      bar.style.width = `${pct}%`;
      bar.className   = `camp-encounter-bar ${barClass}`.trim();
    }
    if (pctEl)   pctEl.textContent = `${pct}%`;
    if (barWrap) {
      barWrap.setAttribute('aria-valuenow', String(pct));
      barWrap.setAttribute('aria-label', `Night encounter chance ${pct}%`);
    }
  }

  /**
   * Update ration display without full re-render.
   */
  _refreshRations() {
    const val   = this.el.querySelector('#camp-rations-val');
    if (!val) return;
    const pts = WorldState.rationPoints ?? 0;
    val.textContent = String(pts);
    val.classList.toggle('low', pts < (this._party.length || 1));
  }

  /**
   * Enable/disable footer buttons based on state.
   */
  _refreshFooterButtons() {
    const restBtn   = this.el.querySelector('#camp-rest-btn');
    const forageBtn = this.el.querySelector('#camp-forage-btn');

    const hasParty   = this._party.length > 0;
    const hasRations = (WorldState.rationPoints ?? 0) > 0;

    if (restBtn) {
      // Allow rest even with 0 rations (just shows warning on use)
      restBtn.disabled = !hasParty;
    }
    if (forageBtn) {
      forageBtn.disabled = !hasParty;
    }
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  /**
   * Apply current role/equipment state to CampingSystem (if available).
   */
  _applyToCampingSystem() {
    try {
      const CS = window.__campingSystem;
      if (!CS) return;

      const roleAssignments = [];
      for (const [charId, role] of this._roleAssignments) {
        if (role) roleAssignments.push({ charId, role });
      }

      if (typeof CS.setupCamp === 'function') {
        CS.setupCamp(roleAssignments, { ...this._equipment });
      }
    } catch (e) {
      DebugLog.warn(`[camp] CampingSystem.setupCamp error: ${e.message}`);
    }
  }

  /**
   * Attempt forage action. Shows result inline.
   */
  _doForage() {
    this._applyToCampingSystem();

    let result = null;
    try {
      const CS = window.__campingSystem;
      if (CS && typeof CS.forage === 'function') {
        const rng = window.__rng ?? null;
        result = CS.forage(this._party, rng);
      }
    } catch (e) {
      DebugLog.warn(`[camp] Forage error: ${e.message}`);
    }

    if (!result) {
      // Local fallback forage roll
      const roll    = Math.floor(Math.random() * 20) + 1;
      const dc      = 10;
      const success = roll >= dc;
      const gained  = success ? (1 + Math.floor(Math.random() * 4) + 1) : 0;
      if (success) WorldState.rationPoints = (WorldState.rationPoints ?? 0) + gained;
      result = {
        success,
        gained,
        roll,
        dc,
        message: success
          ? `Foraging succeeded (rolled ${roll} vs DC ${dc}). Gained ${gained} ration points.`
          : `Foraging failed (rolled ${roll} vs DC ${dc}). Nothing useful found.`,
      };
    }

    DebugLog.event(`[camp] Forage; success=${result.success} gained=${result.gained} roll=${result.roll}`);

    this._setResult(result.message, result.success ? 'success' : 'warn');
    this._refreshRations();
    this._refreshFooterButtons();
  }

  /**
   * Show rest confirmation dialog, then perform rest.
   */
  _promptRest() {
    const rations      = WorldState.rationPoints ?? 0;
    const partySize    = this._party.length || 1;
    const costEstimate = partySize; // 1 per member, cook reduces but we show a safe estimate
    const hasRations   = rations >= costEstimate;

    const overlay = document.createElement('div');
    overlay.className = 'camp-confirm-overlay';
    overlay.id = 'camp-rest-confirm';

    const ratWarning = hasRations
      ? ''
      : `<p style="color:#c83020;font-size:clamp(0.65rem,0.9vw,0.72rem);margin:0">
           Warning: rations may be insufficient. Party could lose HP.
         </p>`;

    overlay.innerHTML = `
      <div class="camp-confirm-card">
        <div class="camp-confirm-title">Rest Until Morning?</div>
        <div class="camp-confirm-desc">
          The party will rest until dawn. Rations will be consumed (est. ${costEstimate} pts).
          Any night encounters will interrupt rest.
        </div>
        ${ratWarning}
        <div class="camp-confirm-actions">
          <button class="camp-btn camp-btn--primary" id="camp-rest-confirm-yes" tabindex="0">Confirm Rest</button>
          <button class="camp-btn" id="camp-rest-confirm-no" tabindex="0">Cancel</button>
        </div>
      </div>
    `;

    this.el.style.position = 'relative';
    this.el.appendChild(overlay);
    this._refreshFocusables();

    const yes = overlay.querySelector('#camp-rest-confirm-yes');
    const no  = overlay.querySelector('#camp-rest-confirm-no');

    if (yes) yes.addEventListener('click', () => {
      overlay.remove();
      this._doRest();
    });
    if (no) no.addEventListener('click', () => {
      overlay.remove();
      this._refreshFocusables();
    });

    if (yes) yes.focus();
  }

  /**
   * Execute rest until morning.
   */
  _doRest() {
    this._applyToCampingSystem();

    let result = null;

    try {
      const CS = window.__campingSystem;
      if (CS && typeof CS.restUntilMorning === 'function') {
        const rng = window.__rng ?? null;
        result = CS.restUntilMorning(rng);
      }
    } catch (e) {
      DebugLog.warn(`[camp] restUntilMorning error: ${e.message}`);
    }

    if (!result) {
      // Local fallback
      const consumed = Math.min(WorldState.rationPoints ?? 0, this._party.length || 1);
      WorldState.rationPoints = Math.max(0, (WorldState.rationPoints ?? 0) - consumed);
      result = {
        rationCost: consumed,
        healed:     false,
        encounter:  null,
        message:    `Rested until morning. Rations consumed: ${consumed}.`,
        warnings:   [],
      };
    }

    DebugLog.event(
      `[camp] Rest; rationCost=${result.rationCost} healed=${result.healed} ` +
      `encounter=${result.encounter !== null}`
    );

    // If an encounter was triggered, close this panel and let EncounterDialog handle it
    if (result.encounter) {
      this._setResult(`${result.message} An encounter has begun!`, 'warn');
      this._refreshRations();
      this._refreshTimeBadge();
      this._refreshFooterButtons();
      EventBus.emit('encounter:begin', { encounter: result.encounter, source: 'night_camp' });
      // Hide panel — encounter system / scene manager will take over
      this.hide();
      return;
    }

    const msgType = result.warnings?.length ? 'warn' : 'success';
    this._setResult(result.message, msgType);
    this._refreshRations();
    this._refreshTimeBadge();
    this._refreshFooterButtons();
  }

  /**
   * Re-render the time badge only (cheap update after rest).
   */
  _refreshTimeBadge() {
    const badge = this.el.querySelector('#camp-time-badge');
    if (!badge) return;
    const { label, isNight } = this._getTimeInfo();
    badge.textContent = label;
    badge.classList.toggle('is-night', isNight);
  }

  /**
   * Break camp and close the panel.
   */
  _doBreakCamp() {
    try {
      const CS = window.__campingSystem;
      if (CS && typeof CS.breakCamp === 'function') {
        CS.breakCamp();
      }
    } catch (_) { /* graceful */ }

    DebugLog.info('[camp] Party broke camp');
    EventBus.emit('camp:exit', {});
    this.hide();
  }

  // ─── Message helper ───────────────────────────────────────────────────────

  /**
   * Display an inline result message.
   * @param {string} text
   * @param {'success'|'error'|'warn'|'info'|''} [type='']
   */
  _setResult(text, type = '') {
    const el = this.el.querySelector('#camp-result');
    if (!el) return;
    el.textContent = text;
    el.className = 'camp-result' + (type ? ' ' + type : '');
  }

  // ─── Event binding ────────────────────────────────────────────────────────

  _bindEvents() {
    // Close button
    const closeBtn = this.el.querySelector('#camp-close-btn');
    if (closeBtn && !closeBtn._campBound) {
      closeBtn.addEventListener('click', () => this.hide());
      closeBtn._campBound = true;
    }

    // Break Camp
    const breakBtn = this.el.querySelector('#camp-break-btn');
    if (breakBtn && !breakBtn._campBound) {
      breakBtn.addEventListener('click', () => this._doBreakCamp());
      breakBtn._campBound = true;
    }

    // Rest Until Morning
    const restBtn = this.el.querySelector('#camp-rest-btn');
    if (restBtn && !restBtn._campBound) {
      restBtn.addEventListener('click', () => this._promptRest());
      restBtn._campBound = true;
    }

    // Forage
    const forageBtn = this.el.querySelector('#camp-forage-btn');
    if (forageBtn && !forageBtn._campBound) {
      forageBtn.addEventListener('click', () => this._doForage());
      forageBtn._campBound = true;
    }

    // Role dropdowns — delegate from body
    const body = this.el.querySelector('#camp-body');
    if (body && !body._campRoleBound) {
      body.addEventListener('change', (e) => {
        const sel = e.target;
        if (!sel.classList.contains('camp-role-select')) return;

        const charId = sel.dataset.charId;
        const role   = sel.value;
        this._roleAssignments.set(charId, role);

        // Update skill hint for this member row
        const hintEl  = this.el.querySelector(`#camp-hint-${CSS.escape(charId)}`);
        const roleOpt = ROLE_OPTIONS.find(r => r.value === role);
        if (hintEl) hintEl.textContent = roleOpt?.hint ?? '';

        // Push to CampingSystem and refresh live chance
        this._applyToCampingSystem();
        this._refreshEncounterChance();

        DebugLog.info(`[camp] Role assigned; charId=${charId} role=${role || 'none'}`);
      });
      body._campRoleBound = true;
    }

    // Equipment checkboxes — delegate from body
    if (body && !body._campEquipBound) {
      body.addEventListener('change', (e) => {
        const cb = e.target;
        if (cb.type !== 'checkbox' || !cb.dataset.equipKey) return;

        const key = cb.dataset.equipKey;
        this._equipment[key] = cb.checked;

        // Mirror to WorldState so CampingSystem picks it up
        if (!WorldState.campEquipment) WorldState.campEquipment = {};
        WorldState.campEquipment[key] = cb.checked;

        this._applyToCampingSystem();
        this._refreshEncounterChance();

        DebugLog.info(`[camp] Equipment toggled; key=${key} value=${cb.checked}`);
      });
      body._campEquipBound = true;
    }
  }

  // ─── Utility ─────────────────────────────────────────────────────────────

  /**
   * Escape a string for use as text content / CSS id segments.
   * @param {string} str
   * @returns {string}
   */
  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── Public init (singleton pattern) ─────────────────────────────────────

  /**
   * Initialize the panel and register EventBus listeners.
   * Call once from main.js.
   */
  init() {
    // Open when 'camp:enter' is emitted (debug console or world systems)
    this._unsub = EventBus.on('camp:enter', () => {
      this.show();
    });

    DebugLog.info('[camp] CampingPanel initialized');
  }
}

// ─── Singleton export ─────────────────────────────────────────────────────────

const campingPanel = new CampingPanel();
export default campingPanel;
